import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import SquareButton from '@/components/SquareButton';
import { postJson } from '@/lib/api';
import { ADD_ONS, BOXES, DELIVERY_COUNTIES, LOCAL_DELIVERY, NO_SHIP_STATES } from '@/lib/constants';

const inputCls = 'mt-2 h-12 border-2 border-[hsl(var(--c-ink))] bg-white px-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[hsl(var(--c-mango-orange))]';
const labelCls = 'eyebrow text-[hsl(var(--c-ink))]/70';

function BoxOrderForm({ defaultBox, onSuccess }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    box_size: defaultBox || 'medium', quantity: 1, add_ons: [],
    fulfillment_method: 'shipping',
    // shipping fields
    shipping_address: '', shipping_city: '', shipping_state: '', shipping_zip: '',
    // local delivery fields
    delivery_county: 'wpb', delivery_address: '', delivery_city: '',
    // metadata
    preferred_date: '', gift_message: '', notes: '', honeypot: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleAddon = (id) =>
    setForm((s) => ({
      ...s,
      add_ons: s.add_ons.includes(id) ? s.add_ons.filter((x) => x !== id) : [...s.add_ons, id],
    }));

  // Order math
  const selectedBox = BOXES.find((b) => b.slug === form.box_size) || BOXES[1];
  const county = DELIVERY_COUNTIES.find((c) => c.id === form.delivery_county) || DELIVERY_COUNTIES[0];
  const subtotal = (selectedBox?.price || 0) * (form.quantity || 1);
  const est = useMemo(() => {
    const method = form.fulfillment_method;
    if (method === 'shipping') return { fee: null, feeLabel: 'Calculated at checkout', total: null };
    if (method === 'pickup') return { fee: 0, feeLabel: 'No pickup fee', total: subtotal };
    // local delivery
    const freeByThreshold = subtotal >= LOCAL_DELIVERY.freeThreshold;
    const fee = freeByThreshold ? 0 : county.fee;
    return {
      fee,
      feeLabel: freeByThreshold ? `FREE (order $${LOCAL_DELIVERY.freeThreshold}+)` : (fee === 0 ? 'FREE' : `$${fee}`),
      total: subtotal + fee,
    };
  }, [form.fulfillment_method, form.delivery_county, subtotal, county.fee]);

  // Approximate weight check for local delivery 10-lb min
  const approxWeightLbs = useMemo(() => {
    const wt = { small: 4.5, medium: 7.5, large: 10.5 }[form.box_size] || 7.5;
    return wt * (form.quantity || 1);
  }, [form.box_size, form.quantity]);
  const belowMin = form.fulfillment_method === 'delivery' && approxWeightLbs < LOCAL_DELIVERY.minWeightLbs;
  const shipsToBanned = form.fulfillment_method === 'shipping'
    && form.shipping_state
    && NO_SHIP_STATES.includes(form.shipping_state.trim().toUpperCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.box_size) {
      toast.error('Please fill in your name, email, and box size.');
      return;
    }
    if (shipsToBanned) {
      toast.error(`We cannot ship to ${form.shipping_state.toUpperCase()} due to agricultural regulations.`);
      return;
    }
    if (belowMin) {
      toast.error(`Local delivery requires a ${LOCAL_DELIVERY.minWeightLbs} lb minimum. Add another box or choose pickup / shipping.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // Backend still expects delivery_method; we also send the newer fulfillment_method + fee for records.
        delivery_method: form.fulfillment_method,
        delivery_fee: est.fee,
        delivery_county_label: form.fulfillment_method === 'delivery' ? county.label : undefined,
        subtotal,
        source_page: 'shop',
      };
      const res = await postJson('/box-orders', payload);
      setSubmitted(true);
      toast.success(res.message || 'Box request received!');
      onSuccess && onSuccess(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-8 text-center" data-testid="box-order-success">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center bg-[hsl(var(--c-leaf))] border-2 border-[hsl(var(--c-ink))]">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h3 className="mt-6 font-heading text-3xl font-extrabold">Request received!</h3>
        <p className="mt-3 text-[hsl(var(--c-ink-soft))]">
          We&rsquo;ll review current harvest availability and reach out to confirm your box, fulfillment, payment, and timing.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5" data-testid="box-order-form">
      <input type="text" name="website" value={form.honeypot} onChange={(e) => update('honeypot', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bo-name" className={labelCls}>Full Name *</Label>
          <Input id="bo-name" data-testid="box-order-name-input" required value={form.name} onChange={(e) => update('name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bo-email" className={labelCls}>Email *</Label>
          <Input id="bo-email" type="email" data-testid="box-order-email-input" required value={form.email} onChange={(e) => update('email', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bo-phone" className={labelCls}>Phone</Label>
          <Input id="bo-phone" data-testid="box-order-phone-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>Box Size *</Label>
          <Select value={form.box_size} onValueChange={(v) => update('box_size', v)}>
            <SelectTrigger data-testid="box-order-size-trigger" className={inputCls}><SelectValue placeholder="Choose a box" /></SelectTrigger>
            <SelectContent>
              {BOXES.map((b) => (<SelectItem key={b.slug} value={b.slug}>{b.name} — ${b.price}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bo-qty" className={labelCls}>Quantity</Label>
          <Input id="bo-qty" type="number" min={1} max={50} data-testid="box-order-quantity-input" value={form.quantity} onChange={(e) => update('quantity', parseInt(e.target.value || '1', 10))} className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>Fulfillment Method *</Label>
          <Select value={form.fulfillment_method} onValueChange={(v) => update('fulfillment_method', v)}>
            <SelectTrigger data-testid="box-order-fulfillment-trigger" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="shipping">Nationwide &amp; Florida Shipping</SelectItem>
              <SelectItem value="pickup">Local Pickup (South Florida)</SelectItem>
              <SelectItem value="delivery">Local Delivery (Tri-County)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* SHIPPING FIELDS */}
      {form.fulfillment_method === 'shipping' && (
        <div className="border-2 border-[hsl(var(--c-ink))]/20 bg-[hsl(var(--c-cream))]/60 p-4 grid gap-4" data-testid="box-order-shipping-fields">
          <div className="eyebrow text-[hsl(var(--c-ink))]/60">Shipping address</div>
          <div>
            <Label htmlFor="bo-ship-addr" className={labelCls}>Street Address</Label>
            <Input id="bo-ship-addr" data-testid="box-order-shipaddr-input" value={form.shipping_address} onChange={(e) => update('shipping_address', e.target.value)} className={inputCls} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bo-ship-city" className={labelCls}>City</Label>
              <Input id="bo-ship-city" data-testid="box-order-city-input" value={form.shipping_city} onChange={(e) => update('shipping_city', e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label htmlFor="bo-ship-state" className={labelCls}>State *</Label>
              <Input id="bo-ship-state" data-testid="box-order-state-input" maxLength={2} placeholder="FL" value={form.shipping_state} onChange={(e) => update('shipping_state', e.target.value.toUpperCase())} className={inputCls} />
            </div>
            <div>
              <Label htmlFor="bo-ship-zip" className={labelCls}>ZIP</Label>
              <Input id="bo-ship-zip" data-testid="box-order-zip-input" value={form.shipping_zip} onChange={(e) => update('shipping_zip', e.target.value)} className={inputCls} />
            </div>
          </div>
          {shipsToBanned && (
            <div className="flex items-start gap-2 border-2 border-[hsl(var(--c-mango-red))] bg-[hsl(var(--c-mango-red))]/8 p-3 text-xs text-[hsl(var(--c-ink))]">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--c-mango-red-deep))] shrink-0" />
              <span>We cannot ship fresh mangoes to <strong>{form.shipping_state}</strong> due to state agricultural regulations. Please choose a different state or fulfillment option.</span>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--c-ink-muted))]">Shipping rates are calculated at checkout. We cannot ship to California, Hawaii, or U.S. territories.</p>
        </div>
      )}

      {/* PICKUP FIELDS */}
      {form.fulfillment_method === 'pickup' && (
        <div className="border-2 border-[hsl(var(--c-ink))]/20 bg-[hsl(var(--c-cream))]/60 p-4 grid gap-4" data-testid="box-order-pickup-fields">
          <div className="eyebrow text-[hsl(var(--c-ink))]/60">Local pickup</div>
          <p className="text-xs text-[hsl(var(--c-ink-soft))]">Pickup available during business operating hours. You&rsquo;ll receive pickup instructions with your order confirmation.</p>
          <div>
            <Label htmlFor="bo-pickup-date" className={labelCls}>Preferred Pickup Date</Label>
            <Input id="bo-pickup-date" type="date" data-testid="box-order-pickup-date-input" value={form.preferred_date} onChange={(e) => update('preferred_date', e.target.value)} className={inputCls} />
          </div>
        </div>
      )}

      {/* LOCAL DELIVERY FIELDS */}
      {form.fulfillment_method === 'delivery' && (
        <div className="border-2 border-[hsl(var(--c-ink))]/20 bg-[hsl(var(--c-cream))]/60 p-4 grid gap-4" data-testid="box-order-delivery-fields">
          <div className="eyebrow text-[hsl(var(--c-ink))]/60">Local delivery</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>County *</Label>
              <Select value={form.delivery_county} onValueChange={(v) => update('delivery_county', v)}>
                <SelectTrigger data-testid="box-order-county-trigger" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_COUNTIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label} — {c.fee === 0 ? 'FREE' : `$${c.fee}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bo-del-city" className={labelCls}>Delivery City</Label>
              <Input id="bo-del-city" data-testid="box-order-delcity-input" value={form.delivery_city} onChange={(e) => update('delivery_city', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <Label htmlFor="bo-del-addr" className={labelCls}>Delivery Address</Label>
            <Input id="bo-del-addr" data-testid="box-order-deladdr-input" value={form.delivery_address} onChange={(e) => update('delivery_address', e.target.value)} className={inputCls} />
          </div>
          {belowMin && (
            <div className="flex items-start gap-2 border-2 border-[hsl(var(--c-mango-orange))] bg-[hsl(var(--c-mango-orange))]/8 p-3 text-xs text-[hsl(var(--c-ink))]" data-testid="box-order-belowmin-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--c-mango-orange-deep))] shrink-0" />
              <span>Local delivery requires a <strong>{LOCAL_DELIVERY.minWeightLbs} lb minimum</strong>. Your current selection is approximately {approxWeightLbs.toFixed(1)} lbs — add another box or choose Pickup / Shipping.</span>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--c-ink-muted))]">FREE delivery on orders $200+. {LOCAL_DELIVERY.minWeightLbs} lb minimum applies across Palm Beach, Broward, and Miami-Dade.</p>
        </div>
      )}

      {/* Order summary */}
      <div className="border-2 border-[hsl(var(--c-ink))] bg-white p-4 text-sm" data-testid="box-order-summary">
        <div className="flex items-center justify-between">
          <span className="text-[hsl(var(--c-ink-soft))]">{selectedBox.name} × {form.quantity}</span>
          <span className="font-heading font-extrabold">${subtotal}</span>
        </div>
        {form.fulfillment_method !== 'shipping' && (
          <div className="mt-1 flex items-center justify-between text-xs text-[hsl(var(--c-ink-soft))] uppercase tracking-wider">
            <span>{form.fulfillment_method === 'delivery' ? `Delivery — ${county.label}` : 'Local pickup'}</span>
            <span className="font-bold text-[hsl(var(--c-ink))]">{est.feeLabel}</span>
          </div>
        )}
        {form.fulfillment_method === 'shipping' && (
          <div className="mt-1 flex items-center justify-between text-xs text-[hsl(var(--c-ink-soft))] uppercase tracking-wider">
            <span>Shipping</span>
            <span className="font-bold text-[hsl(var(--c-ink))]">Calculated at checkout</span>
          </div>
        )}
        {est.total !== null && form.fulfillment_method !== 'shipping' && (
          <div className="mt-3 pt-3 border-t-2 border-[hsl(var(--c-ink))]/20 flex items-center justify-between">
            <span className="eyebrow text-[hsl(var(--c-ink))]/70">Estimated total</span>
            <span className="font-heading text-xl font-extrabold text-[hsl(var(--c-mango-red))]">${est.total}</span>
          </div>
        )}
      </div>

      <div>
        <Label className={labelCls}>Add-Ons</Label>
        <div className="mt-2 grid sm:grid-cols-2 gap-0 border-2 border-[hsl(var(--c-ink))]">
          {ADD_ONS.map((a, i) => (
            <label key={a.id} className={`flex items-start gap-3 bg-white p-4 cursor-pointer hover:bg-[hsl(var(--c-cream-deep))] btn-transition ${i % 2 === 0 ? 'sm:border-r-2 sm:border-[hsl(var(--c-ink))]' : ''} ${i < ADD_ONS.length - 2 ? 'border-b-2 border-[hsl(var(--c-ink))]' : ''}`}>
              <Checkbox checked={form.add_ons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} data-testid={`box-order-addon-${a.id}`} className="mt-0.5" />
              <span className="text-sm font-semibold">{a.label}</span>
            </label>
          ))}
        </div>
      </div>

      {form.add_ons.includes('gift-message') && (
        <div>
          <Label htmlFor="bo-gift" className={labelCls}>Gift Message</Label>
          <Textarea id="bo-gift" data-testid="box-order-gift-input" value={form.gift_message} onChange={(e) => update('gift_message', e.target.value)} className={`${inputCls} h-auto py-3`} rows={3} />
        </div>
      )}

      <div>
        <Label htmlFor="bo-notes" className={labelCls}>Notes</Label>
        <Textarea id="bo-notes" data-testid="box-order-notes-input" value={form.notes} onChange={(e) => update('notes', e.target.value)} className={`${inputCls} h-auto py-3`} rows={3} placeholder="Preferred delivery week, variety preferences, etc." />
      </div>

      <SquareButton type="submit" disabled={submitting || shipsToBanned || belowMin} variant="orange" size="lg" fullWidth testId="box-order-submit-button">
        {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>) : 'Submit Box Request'}
      </SquareButton>
      <p className="text-xs text-[hsl(var(--c-ink-soft))] uppercase tracking-wider">
        No payment is collected here. We&rsquo;ll confirm availability, varieties, and total before charging.
      </p>
    </form>
  );
}

export function BoxOrderDialog({ trigger, defaultBox }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl bg-[hsl(var(--c-cream))] border-2 border-[hsl(var(--c-ink))] max-h-[90vh] overflow-y-auto p-0" data-testid="box-order-dialog">
        <div className="p-6 sm:p-8 border-b-2 border-[hsl(var(--c-ink))] bg-[hsl(var(--c-mango-yellow))]">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl font-extrabold leading-tight">Request your mango box</DialogTitle>
            <DialogDescription className="text-[hsl(var(--c-ink-soft))] mt-2">
              Tell us a little about your order. We’ll confirm availability, pricing, and timing within one business day.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 sm:p-8">
          <BoxOrderForm defaultBox={defaultBox} onSuccess={() => setTimeout(() => setOpen(false), 2500)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BoxOrderForm;
